import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { loginRequest } from 'app/auth.api';
import { setExpectedUserId } from 'app/sessionIdentity';
import { setManagerAccessToken } from 'app/tokens';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertLabel from 'components/HubertUI/HubertLabel';
import { setIsLoggedIn } from 'rtk/ConfigSlice';
import { useAppDispatch } from 'rtk/query/hooks';

const zLoginData = z.object({
  email: z
    .string()
    .email('Invalid email format')
    .transform((x) => x.trim().toLowerCase()),
  password: z.string().min(4, 'Password must contain at least 4 characters'),
});
type TLoginData = z.infer<typeof zLoginData>;

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // On login: Redirect to
  const { search, pathname } = useLocation();

  const [data, setData] = useState<TLoginData>({
    email: '',
    password: '',
  });
  const [isValidData, setIsValidData] = useState<boolean>(true);

  const isValid = (data: TLoginData) => {
    try {
      setData(data);
      zLoginData.parse(data);
      setIsValidData(false);
    } catch {
      setIsValidData(true);
    }
  };

  const submit = () => {
    loginRequest(data.email, data.password)
      .then(async (res: any) => {
        setManagerAccessToken(res.accessToken);
        setExpectedUserId(res.accessToken);
        dispatch(setIsLoggedIn({ user: res }));
        toast.success(`Logged in as ${data.email}`);

        if (pathname && pathname !== '/' && search) {
          const navTo = search.split('redirect=')[1];
          navigate(navTo);
        } else {
          navigate('/');
        }
      })
      .catch((error) => {
        if (typeof error === 'string') {
          toast.error(error);
        } else {
          if ('message' in error) {
            toast.error(error.message);
          } else {
            toast.error('An unknown error occurred');
          }
        }
        navigate('/login');
      });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
        <div>
          <img src="./assets/images/hubert/logo/Lockup/dark/hubert_logo_lockup_dark@2x.png" alt="Hubert logo" />
        </div>

        {/* <h2 className="text-2xl font-bold text-center mb-6">Sign in to Hubert</h2> */}

        <div className="mb-4">
          <HubertLabel htmlFor="email">Email</HubertLabel>
          <input
            type="email"
            id="email"
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder="Enter your email"
            required
            autoFocus
            onChange={(e) => {
              const x = {
                ...data,
                email: e.target.value,
              };
              isValid(x);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                submit();
              }
            }}
          />
        </div>
        <div className="mb-6">
          <HubertLabel htmlFor="password">Password</HubertLabel>
          <input
            type="password"
            id="password"
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder="Enter your password"
            required
            onChange={(e) => {
              const x = {
                ...data,
                password: e.target.value,
              };
              isValid(x);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                submit();
              }
            }}
          />
        </div>
        <div className="flex justify-end">
          <HubertButton
            className=""
            disabled={isValidData}
            onClick={() => {
              submit();
            }}
          >
            Login
          </HubertButton>
        </div>
      </div>
    </div>
  );
};

export default Login;
